<template>
  <div v-if="isLoading" class="text-center py-5">
    <div class="spinner-border text-primary" role="status"></div>
  </div>
  <b-row class="gy-4 scheme-align">
    <fundSearch class="d-none d-md-block mb-2" />
    <b-col cols="12" class="d-block d-md-none mt-0">
      <fundSearch class="my-3" />
    </b-col>
    <b-row>
      <b-col cols="12" lg="7">
        <component :is="headingTag" class="text-gray-900">
          {{ truncatedSchemeName }}
        </component>

        <div class="d-flex flex-wrap gap-2 mt-3">
          <small>
            <b-badge class="py-2 text-muted scheme-background">
              {{ schemeData?.schemedetails?.subcategory }}
            </b-badge>
          </small>

          <div
            class="dropdown"
            style="margin-top: -1px"
            @click="toggleDropdown"
            :class="{ show: isDropdownOpen }"
          >
            <button
              class="btn dropdown-toggle scheme-background py-1"
              type="button"
            >
              {{ selectedPlan }}
            </button>
            <ul
              v-show="isDropdownOpen"
              class="dropdown-menu position-absolute"
              style="z-index: 1050"
            >
              <li v-for="plan in availablePlans" :key="plan">
                <a
                  class="dropdown-item"
                  href="#"
                  @click.stop.prevent="selectPlan(plan)"
                >
                  {{ plan }}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div
          class="mt-3 flex items-center text-gray-700"
          style="font-size: 14px"
        >
          <span>NAV :</span>
          <span class="text-lg fw-bold ml-2 text-gray-900">
            Rs {{ formatToTwoDecimals(schemeData?.schemedetails?.nav) }}
          </span>
          <span :class="['ml-2', 'text-xs', navChangeClass]">
            <span v-if="schemeData?.schemedetails?.navchange_value > 0">▲</span>
            <span v-else-if="schemeData?.schemedetails?.navchange_value < 0"
              >▼</span
            >
            <span v-else>-</span>
            {{
              formatToTwoDecimals(schemeData?.schemedetails?.navchange_value)
            }}
            ({{
              formatToTwoDecimals(schemeData?.schemedetails?.navchange_percent)
            }}%)
          </span>

          <small class="ml-2 text-gray-500 text-xs nav-date">
            As of {{ formattedNavDate }}
          </small>
        </div>
      </b-col>

      <b-col
        cols="12"
        lg="5"
        class="ratings-col px-5 mt-2 d-flex flex-column justify-content-between"
      >
        <b-row
          class="py-5 ratings-card text-center d-flex align-items-stretch mb-1 position-relative"
        >
          <b-col cols="6" class="d-flex flex-column justify-content-center">
            <small class="fw-bold"> Prime Rating</small>
            <small class="stars mb-1">
              <template v-if="isNumericRating">
                <span
                  v-for="(star, index) in generateStars"
                  :key="index"
                  :class="star"
                >
                  <template v-if="star === 'full'">★</template>
                  <template v-else-if="star === 'half'">⯪</template>
                  <template v-else>☆</template>
                </span>
              </template>

              <template v-else>
                {{
                  schemeData?.schemedetails?.prime_rating
                    ? schemeData.schemedetails.prime_rating
                    : 'Unrated'
                }}
              </template>
            </small>
          </b-col>

          <b-col
            cols="6"
            class="inner-border-left d-flex flex-column align-items-center position-relative"
          >
            <small class="fw-bold">Prime Call</small>

            <small
              class="sell-badge mt-1"
              :class="{
              'bg-red':
                schemeData?.schemedetails?.buy_sell_hold &&
                schemeData.schemedetails.buy_sell_hold.split(' ')[0].replace(/[^a-zA-Z]/g, '').toLowerCase() === 'sell',
              'bg-green':
                schemeData?.schemedetails?.buy_sell_hold &&
                schemeData.schemedetails.buy_sell_hold.split(' ')[0].replace(/[^a-zA-Z]/g, '').toLowerCase() === 'buy',
              'bg-yellow':
                schemeData?.schemedetails?.buy_sell_hold &&
                schemeData.schemedetails.buy_sell_hold.split(' ')[0].replace(/[^a-zA-Z]/g, '').toLowerCase() === 'hold',
               }"
            >
            {{
              schemeData?.schemedetails?.buy_sell_hold
              ? schemeData.schemedetails.buy_sell_hold.split(' ')[0].replace(/[^a-zA-Z]/g, '')
              : 'No Opinion'
            }}
            </small>
          </b-col>
        </b-row>  
      </b-col>
    </b-row>
  </b-row>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useStore } from 'vuex'
import fundSearch from './fundSearch.vue'
const store = useStore()
const schemeData = computed(() => store.getters.schemeData)
const headingTag = ref('h1')

const availablePlans = computed(() => {
  return plans.filter((plan) => plan !== selectedPlan.value)
})

const selectedPlan = ref('Direct - Growth')
const plans = ['Regular - Growth', 'Direct - Growth']
const isDropdownOpen = ref(false)
const selectPlan = async (plan) => {
  selectedPlan.value = plan
  isDropdownOpen.value = false
  isLoading.value = true

  try {
    let schemeCode = ''
    if (plan === 'Direct - Growth') {
      await store.dispatch('fetchSchemeData')
      schemeCode = schemeData.value?.schemedetails?.directScheme
    } else if (plan === 'Regular - Growth') {
      const regularSchemeCode = schemeData.value?.schemedetails?.regularScheme
      if (regularSchemeCode) {
        await store.dispatch('fetchSchemeData', regularSchemeCode)
        schemeCode = regularSchemeCode
      } else {
        console.warn('Regular scheme code not found')
        return
      }
    }

    const schemeSlug = truncatedSchemeName.value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()

    const baseUrl = process.env.VUE_APP_BASE_URL
    const fullURL = `${baseUrl}/mutual-funds/fund/${schemeSlug}/${schemeCode}/`
    sessionStorage.setItem('prime_selected_plan', plan)

    if (schemeCode && schemeSlug) {
      window.location.href = fullURL
    }
  } catch (error) {
    console.error('Error fetching scheme data:', error)
  } finally {
    isLoading.value = false
  }
}

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
}

const truncatedSchemeName = computed(() => {
  const schemeName = schemeData.value?.schemedetails?.scheme_name || ''
  const index = schemeName.indexOf('(G)')
  return index !== -1 ? schemeName.slice(0, index).trim() : schemeName.trim()
})
const isLoading = ref(false)

watch(
  schemeData,
  (newVal) => {
    if (newVal && newVal.schemedetails) {
      const planType = schemeData.value?.schemedetails?.plan
      selectedPlan.value =
        planType === '5' ? 'Direct - Growth' : 'Regular - Growth'
    }
  },
  { immediate: true }
)

const formattedNavDate = computed(() => {
  const navDateStr = schemeData.value?.schemedetails?.nav_date
  if (!navDateStr) return ''

  const correctedDate = navDateStr.replace(' ', 'T') + 'Z'
  const date = new Date(correctedDate)

  if (isNaN(date)) return ''
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
})

const navChangeClass = computed(() => {
  const navChange = parseFloat(schemeData.value?.schemedetails?.navchange_value)
  if (isNaN(navChange)) return ''
  return navChange < 0 ? 'text-danger' : 'text-success'
})
const formatToTwoDecimals = (value) => {
  const number = parseFloat(value)
  return isNaN(number) ? '-' : number.toFixed(2)
}

const rating = computed(() => schemeData.value?.schemedetails?.prime_rating)

const isNumericRating = computed(() => {
  return !isNaN(parseFloat(rating.value)) && isFinite(rating.value)
})

const generateStars = computed(() => {
  const stars = []
  const ratingValue = parseFloat(rating.value)
  const fullStars = Math.floor(ratingValue)
  const hasHalfStar = ratingValue % 1 >= 0.5
  const totalStars = 5

  for (let i = 0; i < totalStars; i++) {
    if (i < fullStars) {
      stars.push('full')
    } else if (i === fullStars && hasHalfStar) {
      stars.push('half')
    } else {
      stars.push('empty')
    }
  }
  return stars
})

onMounted(() => {
  const storedPlan = sessionStorage.getItem('prime_selected_plan')
  if (storedPlan && plans.includes(storedPlan)) {
    selectedPlan.value = storedPlan
  }
})

onBeforeUnmount(() => {})


</script>

<style scoped>


.scheme-align {
  margin-top: unset !important;
}

.overlay-lock {
  position: absolute;
  top: 0;
  left: 120px;
  width: calc(100% - 100px);
  height: 100%;
  z-index: 10;
}

.lock-content {
  text-align: center;
}
.subscribe-message {
  font-size: 18px;
  font-weight: bold;
}

.scheme-background {
  background: #eaeaeb !important;
  color: black !important;
  border-radius: 10px !important;
  font-size: 16px;
  box-shadow: unset !important;
}

.ratings-card {
  border-radius: 10px;
  border: 1px solid black;
  background: linear-gradient(to bottom, #00a3d9 0%, #21404b 88%, #263238 100%);
  color: white;
  font-size: 20px;
}

.bg-red {
  background-color: #dd2e44;
  width: 50%;
}

.bg-green {
  background-color: #1fd19f;
  width: 50%;
}

.bg-yellow {
  background-color: #F2F231;
  width: 50%; 
}

.sell-badge {
  border-radius: 5px;
  color: white;
}

@media (min-width: 768px) {
  .border-left-md {
    border-left: 1px solid #ddd;
  }
}

@media (max-width: 768px) {
  .ratings-col {
    padding-left: -1px !important;
    padding-right: 0 !important;
    width: 110% !important;
    max-width: 108% !important;
  }
}

.inner-border-left {
  border-left: 1px solid #ddd;
}

.stars span {
  font-size: 18px;
  margin-right: 2px;
}

.stars .full,
.stars .half {
  color: #f2f231;
}

.dropdown-menu {
  display: none;
}

.dropdown.show .dropdown-menu {
  display: block;
}

@media (max-width: 500px) {
  .nav-date {
    display: block;
    margin-left: unset !important;
    margin-top: 5px;
  }
  .badge.d-flex.align-items-center.bg-secondary {
    flex-direction: row !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100%;
    text-align: center;
    padding: 8px 0 !important;
  }
  .badge.d-flex.align-items-center.bg-secondary img {
    margin-bottom: 0;
    margin-right: 0 !important;
    height: 18px !important;
    width: 18px !important;
  }
  .badge.d-flex.align-items-center.bg-secondary span {
    font-size: 12px !important;
    white-space: normal;
  }
}

.prime-font {
  font-size: 15px;
}

@media (max-width: 450px) {
  .prime-font {
    font-size: 12px;
  }
  .ratings-card {
    font-size: unset;
  }
}

@media (max-width: 360px) {
  .prime-font {
    font-size: 10px;
  }
}
</style>
